import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, Select, Tag, Modal, Form,
  message, Tooltip, Popconfirm, Descriptions, Badge
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, ReloadOutlined, KeyOutlined, LockOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

interface User {
  userId: number;
  username: string;
  realName: string;
  employeeId: string;
  email: string;
  phone: string;
  deptId: number;
  deptName: string;
  deptNameEn: string;
  roleId: number;
  roleName: string;
  roleNameEn: string;
  roleCode: string;
  status: 'active' | 'inactive' | 'locked';
  language: string;
  lastLoginAt: string;
  createdAt: string;
}

const Users: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [searchParams, setSearchParams] = useState<any>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [resetPwdVisible, setResetPwdVisible] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [pwdForm] = Form.useForm();

  const fetchUsers = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        ...searchParams
      });
      
      const res = await fetch(`/api/users?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await res.json();
      if (data.success) {
        setUsers(data.data.list);
        setPagination({ ...pagination, page, pageSize, total: data.data.pagination.total });
      }
    } catch (error) {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchParams]);

  const handleSearch = (keyword: string) => {
    setSearchParams((prev: any) => ({ ...prev, keyword }));
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      language: 'en',
      roleId: 6
    });
    setModalVisible(true);
  };

  const handleEdit = (record: User) => {
    setEditingUser(record);
    form.setFieldsValue({
      username: record.username,
      realName: record.realName,
      employeeId: record.employeeId,
      email: record.email,
      phone: record.phone,
      deptId: record.deptId,
      roleId: record.roleId,
      language: record.language,
      status: record.status
    });
    setModalVisible(true);
  };

  const handleView = (record: User) => {
    setSelectedUser(record);
    setDetailVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const url = editingUser 
        ? `/api/users/${editingUser.userId}`
        : '/api/users';
      
      const res = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(values)
      });

      const data = await res.json();
      if (data.success) {
        message.success(t('common.success'));
        setModalVisible(false);
        fetchUsers(pagination.page, pagination.pageSize);
      } else {
        message.error(data.message || t('common.error'));
      }
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`/api/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await res.json();
      if (data.success) {
        message.success(t('common.success'));
        fetchUsers(pagination.page, pagination.pageSize);
      } else {
        message.error(data.message || t('common.error'));
      }
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  const handleResetPassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      
      const res = await fetch(`/api/users/${resetPwdUser?.userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ newPassword: values.newPassword })
      });

      const data = await res.json();
      if (data.success) {
        message.success(t('common.success'));
        setResetPwdVisible(false);
        pwdForm.resetFields();
      } else {
        message.error(data.message || t('common.error'));
      }
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'success',
      inactive: 'default',
      locked: 'error'
    };
    return colors[status] || 'default';
  };

  const getRoleColor = (code: string) => {
    const colors: Record<string, string> = {
      admin: 'red',
      cost_admin: 'orange',
      ie_engineer: 'blue',
      pe_engineer: 'cyan',
      procurement: 'green',
      readonly: 'default'
    };
    return colors[code] || 'default';
  };

  const columns = [
    {
      title: t('user.username'),
      dataIndex: 'username',
      key: 'username',
      width: 100
    },
    {
      title: t('user.realName'),
      dataIndex: 'realName',
      key: 'realName',
      width: 100
    },
    {
      title: t('user.employeeId'),
      dataIndex: 'employeeId',
      key: 'employeeId',
      width: 80,
      render: (v: string) => v || '-'
    },
    {
      title: t('user.department'),
      dataIndex: 'deptName',
      key: 'deptName',
      width: 120,
      render: (_: any, record: User) => i18n.language === 'zh' ? record.deptName : record.deptNameEn || record.deptName
    },
    {
      title: t('user.role'),
      dataIndex: 'roleName',
      key: 'roleName',
      width: 120,
      render: (_: any, record: User) => (
        <Tag color={getRoleColor(record.roleCode)}>
          {i18n.language === 'zh' ? record.roleName : record.roleNameEn}
        </Tag>
      )
    },
    {
      title: t('user.email'),
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
      render: (v: string) => v || '-'
    },
    {
      title: t('user.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (v: string) => v || '-'
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Badge 
          status={getStatusColor(status) as any} 
          text={t(`user.${status}`) || status} 
        />
      )
    },
    {
      title: t('user.language'),
      dataIndex: 'language',
      key: 'language',
      width: 80,
      render: (v: string) => v === 'zh' ? '中文' : 'EN'
    },
    {
      title: t('user.lastLogin'),
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 150,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: User) => (
        <Space size="small">
          <Tooltip title={t('common.edit')}>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title={t('user.resetPassword')}>
            <Button 
              type="link" 
              size="small" 
              icon={<KeyOutlined />} 
              onClick={() => {
                setResetPwdUser(record);
                setResetPwdVisible(true);
              }} 
            />
          </Tooltip>
          <Popconfirm title={t('common.confirm')} onConfirm={() => handleDelete(record.userId)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Search
            placeholder={`${t('common.search')} ${t('user.username')}/${t('user.realName')}/${t('user.employeeId')}`}
            allowClear
            onSearch={handleSearch}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
          <Select
            style={{ width: 120 }}
            placeholder={t('common.status')}
            allowClear
            onChange={(v) => setSearchParams((prev: any) => ({ ...prev, status: v }))}
          >
            <Option value="active">{t('user.active')}</Option>
            <Option value="inactive">{t('user.inactive')}</Option>
            <Option value="locked">{t('user.locked')}</Option>
          </Select>
          <Select
            style={{ width: 150 }}
            placeholder={t('user.role')}
            allowClear
            onChange={(v) => setSearchParams((prev: any) => ({ ...prev, roleId: v }))}
          >
            <Option value={1}>{t('user.admin')}</Option>
            <Option value={2}>{t('user.costAdmin')}</Option>
            <Option value={3}>{t('user.ieEngineer')}</Option>
            <Option value={4}>{t('user.peEngineer')}</Option>
            <Option value={5}>{t('user.procurement')}</Option>
            <Option value={6}>{t('user.readonly')}</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => fetchUsers()}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('user.addUser')}
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="userId"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} users`,
            onChange: (page, pageSize) => fetchUsers(page, pageSize)
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingUser ? t('user.editUser') : t('user.addUser')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label={t('user.username')}
            rules={[{ required: true, message: `${t('common.required')}` }]}
          >
            <Input disabled={!!editingUser} />
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label={t('auth.password')}
              rules={[{ required: true, message: `${t('common.required')}` }]}
            >
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item
            name="realName"
            label={t('user.realName')}
            rules={[{ required: true, message: `${t('common.required')}` }]}
          >
            <Input />
          </Form.Item>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="employeeId" label={t('user.employeeId')} style={{ width: 250 }}>
              <Input />
            </Form.Item>
            <Form.Item name="roleId" label={t('user.role')} rules={[{ required: true }]} style={{ width: 250 }}>
              <Select>
                <Option value={1}>{t('user.admin')}</Option>
                <Option value={2}>{t('user.costAdmin')}</Option>
                <Option value={3}>{t('user.ieEngineer')}</Option>
                <Option value={4}>{t('user.peEngineer')}</Option>
                <Option value={5}>{t('user.procurement')}</Option>
                <Option value={6}>{t('user.readonly')}</Option>
              </Select>
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="email" label={t('user.email')} style={{ width: 250 }}>
              <Input type="email" />
            </Form.Item>
            <Form.Item name="phone" label={t('user.phone')} style={{ width: 250 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="deptId" label={t('user.department')} style={{ width: 250 }}>
              <Select allowClear>
                <Option value={1}>Finance</Option>
                <Option value={2}>Engineering</Option>
                <Option value={3}>IE</Option>
                <Option value={4}>Procurement</Option>
                <Option value={5}>Production</Option>
                <Option value={6}>Quality</Option>
              </Select>
            </Form.Item>
            <Form.Item name="language" label={t('user.language')} style={{ width: 250 }}>
              <Select>
                <Option value="en">English</Option>
                <Option value="zh">中文</Option>
              </Select>
            </Form.Item>
          </Space>
          {editingUser && (
            <Form.Item name="status" label={t('common.status')}>
              <Select>
                <Option value="active">{t('user.active')}</Option>
                <Option value="inactive">{t('user.inactive')}</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`${t('user.users')} - ${selectedUser?.realName}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={500}
      >
        {selectedUser && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label={t('user.username')}>{selectedUser.username}</Descriptions.Item>
            <Descriptions.Item label={t('user.realName')}>{selectedUser.realName}</Descriptions.Item>
            <Descriptions.Item label={t('user.employeeId')}>{selectedUser.employeeId || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('common.status')}>
              <Badge 
                status={getStatusColor(selectedUser.status) as any} 
                text={selectedUser.status} 
              />
            </Descriptions.Item>
            <Descriptions.Item label={t('user.department')}>
              {i18n.language === 'zh' ? selectedUser.deptName : selectedUser.deptNameEn || selectedUser.deptName}
            </Descriptions.Item>
            <Descriptions.Item label={t('user.role')}>
              <Tag color={getRoleColor(selectedUser.roleCode)}>
                {i18n.language === 'zh' ? selectedUser.roleName : selectedUser.roleNameEn}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('user.email')} span={2}>{selectedUser.email || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('user.phone')}>{selectedUser.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('user.language')}>{selectedUser.language === 'zh' ? '中文' : 'English'}</Descriptions.Item>
            <Descriptions.Item label={t('user.lastLogin')}>
              {selectedUser.lastLoginAt ? dayjs(selectedUser.lastLoginAt).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.createdAt')}>
              {dayjs(selectedUser.createdAt).format('YYYY-MM-DD')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title={`${t('user.resetPassword')} - ${resetPwdUser?.username}`}
        open={resetPwdVisible}
        onOk={handleResetPassword}
        onCancel={() => {
          setResetPwdVisible(false);
          pwdForm.resetFields();
        }}
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label={t('auth.newPassword')}
            rules={[
              { required: true, message: `${t('common.required')}` },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
